import { zodResolver } from "@hookform/resolvers/zod";
import { Dayjs } from "dayjs";
import { useForm } from "react-hook-form";
import {
  EdmGetAllStaffWithGenreAndWorkingHoursDocument,
  useEdmDefineWorkingHoursMutation,
  useEdmDeleteWorkingHoursMutation,
} from "lib/graphql/megaSchema";
import { toaster } from "lib/tools/toaster";
import { WorkingHoursSchemaType, workingHoursFormSchema } from "../tools";

type Props = {
  selectedDate: Dayjs;
  staffId: string;
  handleCloseModal: () => void;
};

export const useStaffDetailsModalForm = ({
  selectedDate,
  staffId,
  handleCloseModal,
}: Props) => {
  const formattedStartDate = selectedDate.format("YYYY-MM-DD");

  const [defineWorkingHours, { loading: createWorkingHoursLoading }] =
    useEdmDefineWorkingHoursMutation({
      refetchQueries: [EdmGetAllStaffWithGenreAndWorkingHoursDocument],
    });

  const [deleteWorkingHours, { loading: deleteWorkingHoursLoading }] =
    useEdmDeleteWorkingHoursMutation({
      refetchQueries: [EdmGetAllStaffWithGenreAndWorkingHoursDocument],
    });

  const {
    control,
    formState: { errors },
    handleSubmit,
  } = useForm<WorkingHoursSchemaType>({
    mode: "onChange",
    defaultValues: {
      hours: undefined,
      minutes: "0",
    },
    resolver: zodResolver(workingHoursFormSchema),
  });

  const onSubmit = async (formData: WorkingHoursSchemaType) => {
    const { hours, minutes } = formData;

    const defineWorkingHoursPromise = defineWorkingHours({
      variables: {
        input: {
          staffId,
          month: formattedStartDate,
          expectedWorkingHours: {
            hours: parseInt(hours),
            minutes: parseInt(minutes),
          },
        },
      },
    });

    await toaster.promise(defineWorkingHoursPromise, {
      success: "Poprawnie dodano godziny pracy",
      loading: "Dodaję godziny pracy...",
      error: "Nie udało się dodać godzin pracy",
    });

    handleCloseModal();
  };

  const onDeleteWorkingHours = async () => {
    const deleteWorkingHoursPromise = deleteWorkingHours({
      variables: {
        input: {
          staffId,
          month: formattedStartDate,
        },
      },
    });

    await toaster.promise(deleteWorkingHoursPromise, {
      success: "Poprawnie usunięto godziny pracy",
      loading: "Usuwam godziny pracy...",
      error: "Nie udało się usunąć godzin pracy",
    });

    handleCloseModal();
  };

  return {
    control,
    errors,
    createWorkingHoursLoading,
    deleteWorkingHoursLoading,
    handleSubmit,
    onDeleteWorkingHours,
    onSubmit,
  };
};
